import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ShieldAlert, AlertOctagon, ExternalLink } from "lucide-react";
import { detectRedFlags, STATUS_COPY } from "@/lib/redFlags";

/**
 * RedFlagsPanel — Precauções Clínicas / Red Flags.
 *
 * Renderiza SEMPRE no final da Análise Clínica. Três estados:
 *   - green:  sem precauções relevantes nos dados registados
 *   - amber:  precauções relativas (adaptação)
 *   - red:    precauções reforçadas (referenciação / adiamento)
 *
 * Linguagem rigorosamente não-diagnóstica em PT-PT. O painel não bloqueia
 * a análise nem o relatório — apenas organiza informação e sugere prudência.
 *
 * Cada flag liga ao card respetivo da MIO Matrix Library (Precauções),
 * preservando contexto de retorno via `?from=workspace&aid=<id>`.
 */

function saveMatrixBackContext(assessmentId) {
  try {
    sessionStorage.setItem("mio:matrix:back", JSON.stringify({
      from: "workspace",
      assessmentId: assessmentId || null,
      scrollY: window.scrollY || window.pageYOffset || 0,
      ts: Date.now(),
    }));
  } catch { /* fallback gracioso */ }
}

function buildMatrixLink(cardId, assessmentId) {
  const p = new URLSearchParams({ section: "precautions", id: cardId });
  if (assessmentId) {
    p.set("from", "workspace");
    p.set("aid", assessmentId);
  }
  return `/library?${p.toString()}`;
}

const STATUS_STYLE = {
  green: {
    container: "border-emerald-500/30 bg-emerald-500/5",
    title: "text-emerald-300",
    icon: ShieldCheck,
    iconCls: "text-emerald-300",
    badge: "border-emerald-500/40 text-emerald-200 bg-emerald-500/10",
    badgeText: "Sem precauções relevantes",
  },
  amber: {
    container: "border-amber-500/35 bg-amber-500/5",
    title: "text-amber-300",
    icon: ShieldAlert,
    iconCls: "text-amber-300",
    badge: "border-amber-500/40 text-amber-200 bg-amber-500/10",
    badgeText: "Precauções relativas",
  },
  red: {
    container: "border-rose-500/40 bg-rose-500/5",
    title: "text-rose-300",
    icon: AlertOctagon,
    iconCls: "text-rose-300",
    badge: "border-rose-500/40 text-rose-200 bg-rose-500/10",
    badgeText: "Atenção sugerida",
  },
};

export default function RedFlagsPanel({ patient, analysis, assessmentId }) {
  const result = useMemo(() => detectRedFlags(patient, analysis), [patient, analysis]);
  const { status, flags } = result;
  const copy = STATUS_COPY[status];
  const style = STATUS_STYLE[status];
  const Icon = style.icon;

  // Agrupa flags por matrixCardId para evitar duplicação visual.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const f of flags) {
      const list = map.get(f.matrixCardId) || [];
      list.push(f);
      map.set(f.matrixCardId, list);
    }
    return [...map.entries()];
  }, [flags]);

  return (
    <div
      data-testid="red-flags-panel"
      data-status={status}
      className={`rounded-2xl border ${style.container} p-6`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${style.iconCls}`} strokeWidth={1.5} />
          <div className={`text-xs uppercase tracking-[0.2em] font-semibold ${style.title}`}>
            Precauções clínicas
          </div>
          <span
            data-testid="red-flags-badge"
            className={`ml-1 text-[9px] px-2 py-0.5 rounded-full border font-mono uppercase tracking-[0.18em] ${style.badge}`}
          >
            {style.badgeText}
          </span>
        </div>
        {status !== "green" && (
          <span data-testid="red-flags-count" className="text-[10px] text-slate-500 font-mono uppercase tracking-[0.18em]">
            {flags.length} sinaliza{flags.length === 1 ? "ção" : "ções"}
          </span>
        )}
      </div>

      <div className="mt-2 text-sm text-slate-200">
        <div className="font-medium text-slate-50">{copy.title}</div>
        <p className="text-slate-300 mt-1.5">{copy.body}</p>
      </div>

      {grouped.length > 0 && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2.5" data-testid="red-flags-list">
          {grouped.map(([cardId, list]) => (
            <div
              key={cardId}
              data-testid={`red-flag-card-${cardId}`}
              className={`rounded-lg border ${list.some(f => f.severity === "red") ? "border-rose-500/30 bg-rose-500/5" : "border-amber-500/30 bg-amber-500/5"} p-3`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="text-slate-100 text-sm font-medium">
                  {list.map(f => f.label).join(" · ")}
                </div>
                <Link
                  to={buildMatrixLink(cardId, assessmentId)}
                  onClick={() => saveMatrixBackContext(assessmentId)}
                  data-testid={`red-flag-matrix-link-${cardId}`}
                  title="Consultar precaução na MIO Matrix"
                  className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] font-mono text-sky-300/80 hover:text-sky-200 transition"
                >
                  <ExternalLink className="w-3 h-3" /> Ver na Matriz
                </Link>
              </div>
              {list.some(f => f.hint) && (
                <div className="text-[11px] text-slate-400 mt-1.5 italic">
                  {list.filter(f => f.hint).map(f => f.hint).join(" · ")}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="text-[10px] text-slate-500 mt-4 italic">{copy.footnote}</div>
    </div>
  );
}
